import { satisfies } from "compare-versions";
import { find, memoize } from "lodash";
import { ShopifyApiVersion } from "../connections/shopify/ShopifyApiVersion";

// To future traveler who want to add or modify the framework version:
// If the change you are making involves breaking changes to the editor, and you think the change will affect other framework versions,
// go to `EditorVersion.ts` to bump the editor version. It will notify all active editors to reload the page.

export const FRAMEWORK_VERSION_CONSTRAINTS = [
  "^0.1.0",
  "^0.2.0",
  "^0.3.0",
  "^0.3.1",
  "^1.0.0",
  "^1.1.0",
  "^1.2.0",
  "^1.3.0",
  "^1.4.0",
  "^1.5.0",
  "^1.6.0",
  "^1.7.0",
] as const;
export type FrameworkVersionConstraint = (typeof FRAMEWORK_VERSION_CONSTRAINTS)[number];

export interface FrameworkVersion {
  constraint: FrameworkVersionConstraint;
  name: string;
  released: boolean;
  beta?: boolean;
  description: string;
  nodeMajorVersion: "16" | "18" | "20" | "22";
  nodeVersion: string;
  fastifyVersion: "3" | "4";
  distroName: "Debian Bullseye" | "Debian Bookworm";
}

export const AvailableVersions: FrameworkVersion[] = [
  {
    constraint: "^0.1.0",
    name: "v0.1",
    released: true,
    description: "Node 16.20, Fastify 3",
    nodeMajorVersion: "16",
    nodeVersion: "16.20.0",
    fastifyVersion: "3",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^0.2.0",
    name: "v0.2",
    released: true,
    description: "Node 18.15, Fastify 3",
    nodeMajorVersion: "18",
    nodeVersion: "18.15.0",
    fastifyVersion: "3",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^0.3.0",
    name: "v0.3",
    released: true,
    description: "Node 20.9, Fastify 4",
    nodeMajorVersion: "20",
    nodeVersion: "20.9.0",
    fastifyVersion: "4",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^0.3.1",
    name: "v0.3.1",
    released: true,
    description: "Node 20.9, Fastify 4, support for Shopify webhook disabling",
    nodeMajorVersion: "20",
    nodeVersion: "20.9.0",
    fastifyVersion: "4",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^1.0.0",
    name: "v1.0.0",
    released: true,
    description: "Node 20.9, Fastify 4, support for source control and multi-environment",
    nodeMajorVersion: "20",
    nodeVersion: "20.9.0",
    fastifyVersion: "4",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^1.1.0",
    name: "v1.1.0",
    released: true,
    beta: false,
    description: "Node 20.12, Fastify 4",
    nodeMajorVersion: "20",
    nodeVersion: "20.12.2",
    fastifyVersion: "4",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^1.2.0",
    name: "v1.2.0",
    released: true,
    beta: false,
    description: "Node 20.12, Fastify 4",
    nodeMajorVersion: "20",
    nodeVersion: "20.12.2",
    fastifyVersion: "4",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^1.3.0",
    name: "v1.3.0",
    released: true,
    description: "Node 20.12, Fastify 4",
    nodeMajorVersion: "20",
    nodeVersion: "20.12.2",
    fastifyVersion: "4",
    distroName: "Debian Bullseye",
  },
  {
    constraint: "^1.4.0",
    name: "v1.4.0",
    released: true,
    description: "Node 22.15, Fastify 4",
    nodeMajorVersion: "22",
    nodeVersion: "22.15.0",
    fastifyVersion: "4",
    distroName: "Debian Bookworm",
  },
  {
    constraint: "^1.5.0",
    name: "v1.5.0",
    released: true,
    description: "Node 22.15, Fastify 4",
    nodeMajorVersion: "22",
    nodeVersion: "22.15.0",
    fastifyVersion: "4",
    distroName: "Debian Bookworm",
  },
  {
    constraint: "^1.6.0",
    name: "v1.6.0",
    released: false,
    description: "Node 22.15, Fastify 4",
    nodeMajorVersion: "22",
    nodeVersion: "22.15.0",
    fastifyVersion: "4",
    distroName: "Debian Bookworm",
  },
  {
    constraint: "^1.7.0",
    name: "v1.7.0",
    released: false,
    description: "Node 22.15, Fastify 4",
    nodeMajorVersion: "22",
    nodeVersion: "22.15.0",
    fastifyVersion: "4",
    distroName: "Debian Bookworm",
  },
];

export function* eachFrameworkVersionConstraint(
  opts: { descending: boolean } = { descending: false }
): Generator<FrameworkVersionConstraint> {
  const list = opts.descending ? [...AvailableVersions].reverse() : AvailableVersions;
  for (const version of list) {
    yield version.constraint;
  }
}

export const latestVersion = memoize(() => AvailableVersions.findLast((version) => version.name != "edge" && version.released)!);

export const secondLatestReleasedVersion = memoize(() => {
  const releasedVersions = AvailableVersions.filter((version) => version.name != "edge" && version.released);

  if (releasedVersions.length < 2) {
    throw new Error("Not enough released versions to determine second latest");
  }
  return releasedVersions[releasedVersions.length - 2];
});

export const edgeVersion = memoize(() => AvailableVersions.at(-1)!);

export const AvailableVersionNames = AvailableVersions.map((version) => version.name);

export const latestConstraint = memoize(() => latestVersion().constraint);
export const edgeConstraint = memoize(() => edgeVersion().constraint);

export const labelForConstraint = (constraint: string) =>
  find(AvailableVersions, { constraint: constraint as FrameworkVersionConstraint })?.name ?? constraint;

export const doesVersionSupportSourceControl = (constraint: string) => {
  return satisfies(constraint, ">=1.0.0");
};
export const doesVersionSupportNamespace = (constraint: string) => {
  return satisfies(constraint, ">=1.1.0");
};
export const doesVersionSupportMetadataGenerator = (constraint: string) => {
  return satisfies(constraint, ">=1.1.0");
};
export const doesVersionSupportGadgetVitePlugin = (constraint: string) => {
  return satisfies(constraint, ">=1.2.0");
};
export const doesVersionSupportModelFieldsInActionParamsType = (constraint: string) => {
  return satisfies(constraint, ">=1.3.0");
};

export const doesVersionSupportHydratedSandboxTypes = (constraint: string) => {
  return satisfies(constraint, ">=1.4.0");
};

export const doesVersionSupportIsolatedDeclarationTypes = (constraint: string) => {
  return satisfies(constraint, ">=1.5.0");
};

export const doesVersionSupportShopifySyncOnlyFields = (constraint: string, shopifyApiVersion: ShopifyApiVersion) => {
  return satisfies(constraint, ">=1.6.0") && shopifyApiVersion >= "2025-04";
};

export const doesVersionSupportShopifyWebhookReconciliationTrigger = (constraint: string) => {
  return satisfies(constraint, ">=1.5.0");
};

export const doesVersionSupportModelMetadataV2 = (constraint: string) => {
  return satisfies(constraint, ">=1.5.0");
};

export const doesVersionSupportSelectiveDBIndexing = (constraint: string) => {
  return satisfies(constraint, ">=1.5.0");
};

export const doesVersionSupportVendoredConnectionPackage = (constraint: string) => {
  return satisfies(constraint, ">=1.5.0");
};

export const doesVersionSupportShopifyAppPlatform = (constraint: string) => {
  return satisfies(constraint, ">=1.7.0");
};

export const getFastifyVersion = (constraint: string) => {
  return find(AvailableVersions, (version) => satisfies(version.constraint, constraint))?.fastifyVersion ?? "4";
};

export const getNodeMajorVersion = (constraint: string) => {
  return find(AvailableVersions, (version) => satisfies(version.constraint, constraint))?.nodeMajorVersion ?? "22";
};

export const getNodeVersion = (constraint: string) => {
  return find(AvailableVersions, (version) => satisfies(version.constraint, constraint))?.nodeVersion ?? "22.15.0";
};

export const getLinuxDistroName = (constraint: string) => {
  return find(AvailableVersions, (version) => satisfies(version.constraint, constraint))?.distroName ?? "Debian Bullseye";
};

export const latestVersionSatisfyingConstraint = (constraint: string) => {
  return AvailableVersions.findLast((version) => satisfies(version.constraint, constraint));
};

export const constraintForLabel = (label: string) => find(AvailableVersions, (item) => item.name === label)?.constraint;

export const AvailableV1Versions: FrameworkVersion[] = AvailableVersions.filter((version) =>
  doesVersionSupportSourceControl(version.constraint)
);
